import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Fullscreen, zoomable, swipeable photo viewer.
 * - Zoom: pinch (touch), double-tap/double-click, mouse wheel. Pan when zoomed.
 * - Navigate: prev/next buttons, swipe (at 1x), arrow keys, thumbnail strip.
 * - Close: X button, Escape, click backdrop.
 *
 * Render only when `photos` is non-empty; pass `onClose` to dismiss.
 */
export default function PhotoLightbox({
  photos,
  startIndex = 0,
  onClose,
  captions,
}: {
  photos: string[];
  startIndex?: number;
  onClose: () => void;
  captions?: (string | null | undefined)[];   // optional per-photo label (e.g. capture time)
}) {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // Mirror transform to refs so gesture handlers read latest values synchronously.
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  scaleRef.current = scale;
  txRef.current = tx;
  tyRef.current = ty;

  const containerRef = useRef<HTMLDivElement>(null);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);

  const MAX = 5;
  const MIN = 1;

  const reset = () => { setScale(1); setTx(0); setTy(0); };

  const go = (dir: 'prev' | 'next') => {
    setIndex((i) => {
      const n = dir === 'next'
        ? (i + 1) % photos.length
        : (i - 1 + photos.length) % photos.length;
      return n;
    });
    reset();
  };

  // Keyboard + browser-back support
  useEffect(() => {
    window.history.pushState({ lightbox: true }, '');
    const onPop = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go('prev');
      else if (e.key === 'ArrowRight') go('next');
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);

  const handleClose = () => {
    // Pop the history entry we pushed (triggers onClose via popstate).
    if (window.history.state?.lightbox) window.history.back();
    else onClose();
  };

  // Container-center-relative coordinates of a client point.
  const rel = (clientX: number, clientY: number) => {
    const r = containerRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return { x: clientX - (r.left + r.width / 2), y: clientY - (r.top + r.height / 2) };
  };

  const clampTranslate = (nx: number, ny: number, s: number) => {
    const r = containerRef.current?.getBoundingClientRect();
    if (!r) return { x: nx, y: ny };
    const maxX = (r.width * (s - 1)) / 2;
    const maxY = (r.height * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, nx)),
      y: Math.max(-maxY, Math.min(maxY, ny)),
    };
  };

  const zoomTo = (newScaleRaw: number, px: number, py: number) => {
    const newScale = Math.max(MIN, Math.min(MAX, newScaleRaw));
    const s = scaleRef.current;
    let nx = px - (px - txRef.current) * (newScale / s);
    let ny = py - (py - tyRef.current) * (newScale / s);
    if (newScale === 1) { nx = 0; ny = 0; }
    const c = clampTranslate(nx, ny, newScale);
    setScale(newScale);
    setTx(c.x);
    setTy(c.y);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      pinchStart.current = { dist, scale: scaleRef.current };
      swipeStart.current = null;
      panStart.current = null;
    } else if (pointers.current.size === 1) {
      if (scaleRef.current > 1) {
        panStart.current = { x: e.clientX, y: e.clientY, tx: txRef.current, ty: tyRef.current };
      } else {
        swipeStart.current = { x: e.clientX, y: e.clientY };
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchStart.current) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const mid = rel((pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2);
      zoomTo(pinchStart.current.scale * (dist / pinchStart.current.dist), mid.x, mid.y);
    } else if (panStart.current) {
      const c = clampTranslate(
        panStart.current.tx + (e.clientX - panStart.current.x),
        panStart.current.ty + (e.clientY - panStart.current.y),
        scaleRef.current,
      );
      setTx(c.x);
      setTy(c.y);
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const sw = swipeStart.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) panStart.current = null;

    // Swipe to navigate (only at 1x)
    if (sw && scaleRef.current === 1) {
      const dx = e.clientX - sw.x;
      const dy = e.clientY - sw.y;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) && photos.length > 1) {
        go(dx < 0 ? 'next' : 'prev');
        swipeStart.current = null;
        return;
      }
    }
    swipeStart.current = null;

    // Double-tap to toggle zoom
    const now = Date.now();
    const lt = lastTap.current;
    if (lt && now - lt.t < 300 && Math.abs(e.clientX - lt.x) < 30 && Math.abs(e.clientY - lt.y) < 30) {
      const p = rel(e.clientX, e.clientY);
      zoomTo(scaleRef.current > 1 ? 1 : 2.5, p.x, p.y);
      lastTap.current = null;
    } else {
      lastTap.current = { t: now, x: e.clientX, y: e.clientY };
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    const p = rel(e.clientX, e.clientY);
    zoomTo(scaleRef.current * (e.deltaY < 0 ? 1.15 : 1 / 1.15), p.x, p.y);
  };

  const btn: React.CSSProperties = {
    position: 'absolute',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    borderRadius: '50%',
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 2,
    color: 'white',
  };

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000,
        touchAction: 'none', userSelect: 'none',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <button onClick={handleClose} style={{ ...btn, top: 16, right: 16 }} aria-label="Close">
        <X size={24} />
      </button>

      {photos.length > 1 && (
        <div style={{
          position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)',
          color: 'white', fontSize: 15, fontWeight: 600,
          background: 'rgba(0,0,0,0.5)', padding: '6px 14px', borderRadius: 20, zIndex: 2,
        }}>
          {index + 1} / {photos.length}
        </div>
      )}

      {captions?.[index] && (
        <div style={{
          position: 'absolute', top: photos.length > 1 ? 52 : 20, left: '50%', transform: 'translateX(-50%)',
          color: 'white', fontSize: 13, fontWeight: 600, background: 'rgba(0,0,0,0.55)',
          padding: '5px 12px', borderRadius: 16, zIndex: 2, whiteSpace: 'nowrap', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {captions[index]}
        </div>
      )}

      {photos.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); go('prev'); }} style={{ ...btn, left: 16, top: '50%', transform: 'translateY(-50%)' }} aria-label="Previous">
          <ChevronLeft size={28} />
        </button>
      )}

      <div
        ref={containerRef}
        style={{
          width: '100%', height: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', overflow: 'hidden',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      >
        <img
          src={photos[index]}
          alt={`Photo ${index + 1}`}
          draggable={false}
          style={{
            maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transition: pointers.current.size ? 'none' : 'transform 0.15s ease-out',
            cursor: scale > 1 ? 'grab' : 'zoom-in',
          }}
        />
      </div>

      {photos.length > 1 && (
        <button onClick={(e) => { e.stopPropagation(); go('next'); }} style={{ ...btn, right: 16, top: '50%', transform: 'translateY(-50%)' }} aria-label="Next">
          <ChevronRight size={28} />
        </button>
      )}

      {photos.length > 1 && (
        <div style={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: 8, background: 'rgba(0,0,0,0.5)', padding: 8,
          borderRadius: 12, maxWidth: '90%', overflowX: 'auto', zIndex: 2,
        }}>
          {photos.map((p, i) => (
            <img
              key={i}
              src={p}
              alt={`Thumbnail ${i + 1}`}
              onClick={(e) => { e.stopPropagation(); setIndex(i); reset(); }}
              style={{
                width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'pointer',
                flexShrink: 0,
                border: i === index ? '2px solid white' : '2px solid transparent',
                opacity: i === index ? 1 : 0.6,
              }}
            />
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
